import React, { useEffect, useRef, useState } from 'react';
import styles from './continuingEducation.module.css';
import { useSelector } from 'react-redux';
import {
  getUser,
  getUserLoading,
  canViewContinuingEducation,
} from '../../data/selectors/userAuthSelectors';
import LoadingSpinner from '../loadingSpinner/LoadingSpinner';
import { assetUrl } from '../../utils/assetsMap';

export default function ContinuingEducation() {
  const userLoading = useSelector(getUserLoading);
  const user = useSelector(getUser);
  const archiveRef = useRef(null);
  const [isArchiveExpanded, setIsArchiveExpanded] = useState(false);

  useEffect(() => {
    document.title = 'KNOW - Continuing Education';
  }, []);

  useEffect(() => {
    // cleanup any existing script
    document.getElementById('archiveScript')?.remove();

    if (!archiveRef.current) return;

    // Create container and append to the specific ref (React won't touch its children)
    const archiveDiv = document.createElement('div');
    archiveDiv.id = 'archiveList';
    archiveDiv.setAttribute('data-archive-count', '200');
    archiveDiv.setAttribute('data-m', 'a07edbx0e860');
    archiveRef.current.appendChild(archiveDiv);

    // Create script and append to body so it runs
    const script = document.createElement('script');
    script.id = 'archiveScript';
    script.src = 'https://static.ctctcdn.com/js/archive-static/current/archive-static.min.js';
    script.onload = () => {
      // Re-trigger common init events
      document.dispatchEvent(new Event('DOMContentLoaded', { bubbles: true }));
      window.dispatchEvent(new Event('load'));
    };
    script.onerror = (e) => console.error('[CTCT] script failed to load', e);

    document.body.appendChild(script);

    return () => {
      // Cleanup: remove script and div
      document.getElementById('archiveScript')?.remove();
      archiveDiv.remove();
    };
  }, []);

  if (userLoading) {
    return <LoadingSpinner />;
  }

  if (!canViewContinuingEducation(user)) {
    return (
      <div className={styles.ceContainer}>
        <h1>You must be logged in as an OncANP member to view CE links.</h1>
      </div>
    );
  }

  return (
    <div className={styles.ceContainer} role="main" aria-label="continuing education" lang="en">
      <h1>Continuing Education</h1>

      <section className={styles.ceSection} aria-labelledby="ce-courses">
        <h2 id="ce-courses">Continuing Education Courses</h2>

        <div className={styles.courseCards}>
          <div className={styles.courseCard}>
            <h3>Get in the KNOW - Acupuncture in Oncology</h3>
            <p>1 hour of continuing education credits, 1 Pharm CE</p>
            <a
              href="https://www.mytestcom.net/app/myTestcomURL.cfm?accountID=c1f6p772e98qw32&assignContentCode=z4132374"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.courseLink}
            >
              Access Activity<span className={styles.srOnly}> (opens in new tab)</span>
            </a>
          </div>

          <div className={styles.courseCard}>
            <h3>Get in the KNOW - Fasting Mimicking Diet (FMD) in Oncology</h3>
            <p>1 hour of continuing education credits, 1 Pharm CE</p>
            <a
              href="https://www.mytestcom.net/app/myTestcomURL.cfm?accountID=c1f6p772e98qw32&assignContentCode=z8132378"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.courseLink}
            >
              Access Activity<span className={styles.srOnly}> (opens in new tab)</span>
            </a>
          </div>

          <div className={styles.courseCard}>
            <h3>Get in the KNOW - Updates in Pediatric Integrative Oncology</h3>
            <p>1 hour of continuing education credits, 1 Pharm CE</p>
            <a
              href="https://www.mytestcom.net/app/myTestcomURL.cfm?accountID=c1f6p772e98qw32&assignContentCode=zh132379"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.courseLink}
            >
              Access Activity<span className={styles.srOnly}> (opens in new tab)</span>
            </a>
          </div>

          <div className={styles.courseCard}>
            <h3>Get in the KNOW - Cannabis in Modern Oncology Care</h3>
            <p>1 hour of continuing education credits, 1 Pharm CE</p>
            <a
              href="https://www.mytestcom.net/app/myTestcomURL.cfm?accountID=c1f6p772e98qw32&assignContentCode=z1132380"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.courseLink}
            >
              Access Activity<span className={styles.srOnly}> (opens in new tab)</span>
            </a>
          </div>

          <div className={styles.courseCard}>
            <h3>Get in the KNOW - Cervical Cancer Awareness</h3>
            <p>2 hours of continuing education credits, 2 Pharm CE</p>
            <a
              href="https://www.mytestcom.net/app/myTestcomURL.cfm?accountID=c1f6p772e98qw32&assignContentCode=za132381"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.courseLink}
            >
              Access Activity<span className={styles.srOnly}> (opens in new tab)</span>
            </a>
          </div>

          <div className={styles.courseCard}>
            <h3>Get in the KNOW - Food as Medicine</h3>
            <p>1 hour of continuing education credits, 1 Pharm CE</p>
            <a
              href="https://www.mytestcom.net/app/myTestcomURL.cfm?accountID=c1f6p772e98qw32&assignContentCode=zz132382"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.courseLink}
            >
              Access Activity<span className={styles.srOnly}> (opens in new tab)</span>
            </a>
          </div>
        </div>
      </section>

      <section className={styles.ceSection} aria-labelledby="email-archive">
        <h2 id="email-archive">Newsletter Archive</h2>
        {/* Begin CTCT Archive */}
        <div
          className={`${styles.archiveContainer} ${!isArchiveExpanded ? styles.collapsedArchive : ''}`}
          ref={archiveRef}
        ></div>
        <button
          className={styles.archiveToggleBtn}
          onClick={() => setIsArchiveExpanded(!isArchiveExpanded)}
          aria-expanded={isArchiveExpanded}
        >
          {isArchiveExpanded ? 'Show Less' : 'Show More'}
        </button>
        {/* End CTCT Archive */}
      </section>

      <section className={styles.ceSection} aria-labelledby="past-ce">
        <h2 id="past-ce">Past Continuing Education</h2>
        <p className={styles.pastCeIntro}>
          While these activities are no longer available for CE credit, they remain accessible for
          educational purposes.
        </p>

        <div className={styles.courseCards}>
          <div className={styles.courseCard}>
            <h3>Natural Support for Surgery, Radiation, Chemo & Hormone Therapies</h3>
            <p>
              Learn about natural approaches to support patients undergoing conventional cancer
              treatments.
            </p>
            <a
              href="https://www.mytestcom.net/app/myTestcomURL.cfm?accountID=c1f6p772e98qw32&assignContentCode=z8110188"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.courseLink}
            >
              Access Activity<span className={styles.srOnly}> (opens in new tab)</span>
            </a>
          </div>

          <div className={styles.courseCard}>
            <h3>TCM Support</h3>
            <p>
              Explore Traditional Chinese Medicine approaches to cancer care and patient support.
            </p>
            <a
              href="https://www.mytestcom.net/app/myTestcomURL.cfm?accountID=c1f6p772e98qw32&assignContentCode=z4110314"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.courseLink}
            >
              Access Activity<span className={styles.srOnly}> (opens in new tab)</span>
            </a>
          </div>

          <div className={styles.courseCard}>
            <h3>Getting to KNOW Side Effect Management of Small Molecule Inhibitors</h3>
            <p>
              2 hours pharm CE - Comprehensive guide to managing side effects of targeted therapies.
            </p>
            <a
              href="https://forms.gle/ao6k8hkADSYqJ9NG9"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.courseLink}
            >
              Access Activity<span className={styles.srOnly}> (opens in new tab)</span>
            </a>
          </div>

          <div className={styles.courseCard}>
            <h3>Up-to-Date Research on Chemotherapy-Herb Interactions</h3>
            <p>
              2 hours pharm CE - Latest evidence on herb-chemotherapy interactions and safety
              considerations.
            </p>
            <a
              href="https://forms.gle/rHGq47UEYMUpcBYv6"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.courseLink}
            >
              Access Activity<span className={styles.srOnly}> (opens in new tab)</span>
            </a>
          </div>

          <div className={styles.courseCard}>
            <h3>Up-to-Date Research on Xeloda</h3>
            <p>
              2 hours pharm CE - Current research on Xeloda (capecitabine) and clinical
              applications.
            </p>
            <a
              href="https://forms.gle/UUBpyUbBdRQvL1FS9"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.courseLink}
            >
              Access Activity<span className={styles.srOnly}> (opens in new tab)</span>
            </a>
          </div>
        </div>
      </section>

      <section className={styles.ceSection} aria-labelledby="monographs">
        <h2 id="monographs">Monographs for Healthcare Providers</h2>

        <div className={styles.monographCards}>
          <a
            href="https://thechi.ca/wp-content/uploads/2024/05/Mistletoe-Extracts-Monograph-Healthcare-Provider-Resource-February-2024.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.monographCard}
          >
            <h3>Mistletoe Extracts</h3>
            <span className={styles.monographMeta}>February 2024</span>
            <span className={styles.monographAction}>
              View PDF<span className={styles.srOnly}> (opens in new tab)</span>
            </span>
          </a>

          <a
            href="https://thechi.ca/wp-content/uploads/2024/05/Artesunate-Monograph-Healthcare-Provider-Resource-January-2024.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.monographCard}
          >
            <h3>Artesunate</h3>
            <span className={styles.monographMeta}>January 2024</span>
            <span className={styles.monographAction}>
              View PDF<span className={styles.srOnly}> (opens in new tab)</span>
            </span>
          </a>

          <a
            href="https://thechi.ca/wp-content/uploads/2024/05/IV-ALA-Monograph-Healthcare-Provider-Resource-January-2024.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.monographCard}
          >
            <h3>IV ALA</h3>
            <span className={styles.monographMeta}>January 2024</span>
            <span className={styles.monographAction}>
              View PDF<span className={styles.srOnly}> (opens in new tab)</span>
            </span>
          </a>

          <a
            href="https://thechi.ca/wp-content/uploads/2024/05/Hyperthermia-Monograph-Healthcare-Provider-Resource-Dec-2023.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.monographCard}
          >
            <h3>Hyperthermia</h3>
            <span className={styles.monographMeta}>December 2023</span>
            <span className={styles.monographAction}>
              View PDF<span className={styles.srOnly}> (opens in new tab)</span>
            </span>
          </a>

          <a
            href="https://thechi.ca/wp-content/uploads/2024/05/IVC-Monograph-Healthcare-Provider-Resource-February-2023.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.monographCard}
          >
            <h3>IVC</h3>
            <span className={styles.monographMeta}>February 2023</span>
            <span className={styles.monographAction}>
              View PDF<span className={styles.srOnly}> (opens in new tab)</span>
            </span>
          </a>
        </div>
      </section>

      <section className={styles.ceSection} aria-labelledby="archives">
        <h2 id="archives">Archives</h2>
        <article className={styles.archiveItem} aria-label="Top 50 Systematic Reviews">
          <h3>Top 50 Systematic Reviews</h3>
          <a href={assetUrl('TOP_50_SYS_REVIEWS')} rel="noopener noreferrer" target="_blank">
            <img
              className={styles.archiveThumb}
              src={assetUrl('TOP_50_SYS_REVIEWS_THUMB')}
              alt="Top 50 Systematic Reviews"
            />
          </a>
        </article>
      </section>
    </div>
  );
}
